/**
 * ResetPasswordPage — handles `/reset-password?token=...`.
 *
 * Token comes from the URL. User picks a new password (min 8, matching
 * the register policy). On success the backend invalidates every active
 * session for the user, so we send them to the login page rather than
 * trying to keep them signed in.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, AlertCircle, CheckCircle2 } from "lucide-react";
import { authService } from "@/services";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  usePageTitle("Reset password");
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState(token ? "form" : "missing");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (password !== confirm) {
      setError(t("resetPassword.errors.mismatch"));
      return;
    }
    if (password.length < 8) {
      setError(t("resetPassword.errors.tooShort"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setState("success");
    } catch (err) {
      setState("failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center">
        {state === "missing" && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("resetPassword.missing.title")}</h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">{t("resetPassword.missing.description")}</p>
            <Link to="/login" className="block mt-6">
              <Button className="w-full h-11 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold">
                {t("resetPassword.missing.backToSignIn")}
              </Button>
            </Link>
          </>
        )}

        {state === "form" && (
          <>
            <h1 className="text-2xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
              {t("resetPassword.form.title")}
            </h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
              {t("resetPassword.form.instructions")}
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-3 text-left">
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                <Input
                  data-testid="reset-password"
                  type="password"
                  placeholder={t("resetPassword.field.password")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                  required
                  minLength={8}
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 w-4 h-4 text-[#1A237E]/30 dark:text-[#E3F2FD]/30" />
                <Input
                  data-testid="reset-password-confirm"
                  type="password"
                  placeholder={t("resetPassword.field.confirm")}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="pl-10 bg-white/60 dark:bg-slate-900/60 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 rounded-xl h-11"
                  required
                  minLength={8}
                />
              </div>
              {error && (
                <p data-testid="reset-error" className="text-xs text-red-600">
                  {error}
                </p>
              )}
              <Button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full h-12 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold"
              >
                {loading ? t("resetPassword.button.saving") : t("resetPassword.button.save")}
              </Button>
            </form>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("resetPassword.success.title")}</h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
              {t("resetPassword.success.description")}
            </p>
            <Button
              data-testid="reset-go-login"
              className="mt-6 w-full h-11 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold"
              onClick={() => navigate("/login")}
            >
              {t("resetPassword.button.goLogin")}
            </Button>
          </>
        )}

        {state === "failed" && (
          <>
            <AlertCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="mt-4 text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD]">{t("resetPassword.failed.title")}</h1>
            <p className="mt-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
              {t("resetPassword.failed.description")}
            </p>
            <Link to="/forgot-password" className="block mt-6">
              <Button className="w-full h-11 rounded-full bg-[#38B6FF] hover:bg-[#1A237E] text-white font-bold">
                {t("resetPassword.button.requestNew")}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
